/**
 * Entrega y lectura de archivos de respaldo (Req. 6.3, 6.4) y de rutinas (Req. 27).
 * Origen: Control de Negocio de Comida; los títulos al compartir son parámetros.
 *
 * Tres vias independientes a proposito, porque en iOS ninguna es infalible:
 *
 *   1. Descarga directa con Blob y enlace `download`   -> via principal
 *   2. Compartir al sistema, si canShare lo permite    -> opcional, ademas
 *   3. Mostrar el JSON en un campo de texto copiable   -> ultimo recurso
 *
 * `navigator.share` con archivos ha sido inestable en iOS: hay reportes de que
 * comparte el texto y descarta el archivo. Por eso no puede ser la unica via.
 *
 * La File System Access API no existe en Safari, asi que no hay forma de
 * guardar respaldos de manera automatica. El respaldo es manual por diseno, y
 * de ahi la importancia del recordatorio.
 */

export type ViaEntrega = 'descarga' | 'compartir';

/** Indica si el dispositivo puede compartir archivos al sistema operativo. */
export function puedeCompartirArchivos(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.share !== 'function') return false;
  if (typeof navigator.canShare !== 'function') return false;

  try {
    const prueba = new File(['prueba'], 'prueba.json', { type: 'application/json' });
    return navigator.canShare({ files: [prueba] });
  } catch {
    return false;
  }
}

/**
 * Descarga un contenido binario ya preparado.
 * En iOS el archivo queda en la app Archivos.
 */
export function descargarBlob(nombre: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);

  try {
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = nombre;
    enlace.rel = 'noopener';
    enlace.style.display = 'none';
    document.body.appendChild(enlace);
    enlace.click();
    enlace.remove();
  } finally {
    // Se libera despues de un momento: revocar de inmediato puede cancelar la
    // descarga en algunos navegadores.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}

/** Descarga texto como archivo. */
export function descargarArchivo(
  nombre: string,
  contenido: string,
  tipo = 'application/json',
): void {
  descargarBlob(nombre, new Blob([contenido], { type: `${tipo};charset=utf-8` }));
}

/**
 * Comparte un contenido binario con el menu del sistema, para enviarlo a
 * Archivos, iCloud, WhatsApp o correo.
 *
 * Devuelve false si el sistema no lo permitio o si la usuaria cancelo, para
 * que la pantalla pueda ofrecer otra via sin dar por hecho que funciono.
 */
export async function compartirBlob(
  nombre: string,
  blob: Blob,
  titulo = 'DuoFit',
): Promise<boolean> {
  if (!puedeCompartirArchivos()) return false;

  try {
    const archivo = new File([blob], nombre, { type: blob.type });
    await navigator.share({ files: [archivo], title: titulo });
    return true;
  } catch (error) {
    // AbortError significa que la usuaria cerro el menu: no es una falla.
    if (error instanceof Error && error.name === 'AbortError') return false;
    return false;
  }
}

/** Comparte texto como archivo. El título cambia entre respaldos y rutinas. */
export async function compartirArchivo(
  nombre: string,
  contenido: string,
  tipo = 'application/json',
  titulo = 'Respaldo de DuoFit',
): Promise<boolean> {
  return compartirBlob(nombre, new Blob([contenido], { type: tipo }), titulo);
}

/**
 * Lee un archivo seleccionado como bytes. Es la via para los respaldos con
 * fotos, que son un ZIP: leerlos como texto los destruiria.
 */
export async function leerArchivoComoBytes(archivo: File): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array(await archivo.arrayBuffer());
}

/** Lee un archivo seleccionado con `<input type="file">` como texto. */
export function leerArchivoComoTexto(archivo: File): Promise<string> {
  return new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    lector.onload = () => {
      const resultado = lector.result;
      if (typeof resultado === 'string') resolver(resultado);
      else rechazar(new Error('No se pudo leer el archivo como texto.'));
    };
    lector.onerror = () => rechazar(new Error('No se pudo leer el archivo seleccionado.'));
    lector.readAsText(archivo, 'utf-8');
  });
}

/** Copia texto al portapapeles. Ultimo recurso para el respaldo. */
export async function copiarAlPortapapeles(texto: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText !== undefined) {
      await navigator.clipboard.writeText(texto);
      return true;
    }
  } catch {
    // Continua con el respaldo de seleccion manual.
  }
  return false;
}
